import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Pressable,
  useMediaQuery,
  Checkbox,
  Input,
  Button,
  Badge,
  Avatar,
  Tooltip
} from 'native-base';
import { COLORS } from '../../utils/colors';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../utils/fonts';
import { MOBILE_VIEW_WIDTH } from '../../utils/screens';
import CustomButton from '../button';
import { Svg } from 'react-native-svg';
import { partnerMailSVG, editIconDetailsPage, deleteCardsIconSVG } from '../../utils/svg';
import { Toaster } from '../toaster';
import { LoaderAnimation } from '../spinner';
import './style.css';

// Custom sort icons
const SortIcon = ({ direction, isActive }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 6L8 2L12 6M4 10L8 14L12 10"
      stroke={isActive ? "#F37032" : "#9CA3AF"}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const SortAscIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 10L8 6L12 10"
      stroke="#F37032"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const SortDescIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 6L8 10L12 6"
      stroke="#F37032"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const HotelTable = ({
  hotelList = [],
  onEditHotel = () => { },
  onDeleteHotel = () => { },
  onEmailEnquiry = () => { },
  refreshHotelList = () => { },
  scrollToTop = () => { },
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => { },
  resetSelection = false,
  pagination = null
}) => {
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
  const toaster = Toaster();

  // State for table functionality
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredHotels, setFilteredHotels] = useState(hotelList);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Update filtered hotels when hotelList changes
  useEffect(() => {
    setFilteredHotels(hotelList);
  }, [hotelList]);

  // Filter hotels based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredHotels(hotelList);
    } else {
      const filtered = hotelList.filter(hotel =>
        hotel.hotel_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.hotel_chain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.hotel_spoc?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.spoc_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.spoc_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.banquet_venue?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.cluster?.toString().includes(searchQuery) ||
        hotel.theatre?.toString().includes(searchQuery) ||
        // Search in head_count array
        (hotel.head_count && hotel.head_count.some(entry =>
          entry.head_count?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.rate?.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
      setFilteredHotels(filtered);
    }
  }, [searchQuery, hotelList]);

  // Reset selections when resetSelection is true
  useEffect(() => {
    if (resetSelection) {
      setSelectedHotels([]);
      setSelectAll(false);
    }
  }, [resetSelection]);

  // Handle select all functionality
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedHotels([]);
      setSelectAll(false);
    } else {
      setSelectedHotels(filteredHotels.map(hotel => hotel._id));
      setSelectAll(true);
    }
  };

  // Handle individual hotel selection
  const handleHotelSelection = (hotelId) => {
    setSelectedHotels(prev => {
      if (prev.includes(hotelId)) {
        const newSelection = prev.filter(id => id !== hotelId);
        setSelectAll(false);
        return newSelection;
      } else {
        const newSelection = [...prev, hotelId];
        setSelectAll(newSelection.length === filteredHotels.length);
        return newSelection;
      }
    });
  };

  // Handle email enquiry for selected hotels
  const handleEmailEnquiry = () => {
    if (selectedHotels.length === 0) {
      toaster('Please select at least one hotel', 'warning', 'warning');
      return;
    }

    // If all hotels are selected, send "all" as string
    const hotelIds = selectAll ? "all" : selectedHotels;
    onEmailEnquiry(hotelIds);
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort filtered hotels
  const getSortedHotels = () => {
    if (!sortConfig.key) return filteredHotels;

    return [...filteredHotels].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle string values only (no special handling for cluster, theatre, head_count)
      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedHotels = getSortedHotels();

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <SortIcon direction="both" isActive={false} />;
    }
    return sortConfig.direction === 'asc' ? <SortAscIcon /> : <SortDescIcon />;
  };

  // Helper function to get dishes breakdown
  const getDishesBreakdown = (dishes) => {
    if (!dishes) return { total: 0, breakdown: [] };

    const breakdown = Object.entries(dishes)
      .filter(([_, count]) => count > 0)
      .map(([category, count]) => ({ category, count }));

    const total = breakdown.reduce((sum, item) => sum + item.count, 0);

    return { total, breakdown };
  };

  // Helper function to get head count breakdown
  const getHeadCountBreakdown = (headCount) => {
    if (!headCount || headCount.length === 0) return { total: 0, breakdown: [] };

    const breakdown = headCount.map((entry, index) => ({
      id: index,
      headCount: entry.head_count || '',
      rate: entry.rate || ''
    }));

    return { total: breakdown.length, breakdown };
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to get full dishes text for tooltip
  const getDishesTooltipText = (dishes) => {
    if (!dishes) return 'No dishes available';

    const breakdown = Object.entries(dishes)
      .filter(([_, count]) => count > 0)
      .map(([category, count]) => `${category}: ${count}`)
      .join('\n');

    return breakdown || 'No dishes available';
  };

  // Mobile view - card layout
  if (MobileWindow) {
    return (
      <VStack space={4} w="100%">
        {/* Search and Actions */}
        <VStack space={3}>
          <Input
            placeholder="Search"
            value={searchQuery}
            onChangeText={setSearchQuery}
            size="md"
            borderRadius="lg"
          />

          <HStack justifyContent="space-between" alignItems="center">
            {selectedHotels.length > 0 && (
              <CustomButton
                text={`Send Email (${selectedHotels.length})`}
                onPress={handleEmailEnquiry}
                bgColor={COLORS.THEME_COLOR}
                textColor={COLORS.WHITE_COLOR}
                size="sm"
              />
            )}
          </HStack>
        </VStack>

        {/* Hotel Cards */}
        {sortedHotels.map((hotel) => {
          const dishesInfo = getDishesBreakdown(hotel.dishes);
          const dishesTooltipText = getDishesTooltipText(hotel.dishes);
          const headCountInfo = getHeadCountBreakdown(hotel.head_count);
          return (
            <Box
              key={hotel._id}
              bg={COLORS.WHITE_COLOR}
              borderRadius="xl"
              p={4}
              borderWidth={1}
              borderColor={COLORS.BORDER_COLOR}
              shadow={2}
            >
              <HStack justifyContent="space-between" alignItems="flex-start" mb={3}>
                <HStack space={3} alignItems="center" flex={1}>
                  <Checkbox
                    isChecked={selectedHotels.includes(hotel._id)}
                    onChange={() => handleHotelSelection(hotel._id)}
                    value={hotel._id}
                    colorScheme="orange"
                  />
                  <VStack flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                      color={COLORS.BLACK_COLOR}
                    >
                      {hotel.hotel_name || '-'}
                    </Text>
                    <Text
                      fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                      color={COLORS.GRAY_COLOR}
                    >
                      {'(' + hotel.hotel_chain + ')' || '-'}
                    </Text>
                  </VStack>
                </HStack>

                <HStack space={2}>
                  <Pressable onPress={() => onEditHotel(hotel)}>
                    <Avatar
                      bg={'none'}
                      size={'sm'}
                    >
                      {editIconDetailsPage}
                    </Avatar>
                  </Pressable>
                  <Pressable onPress={() => onDeleteHotel(hotel)}>
                    <Avatar
                      bg={'none'}
                      size={'sm'}
                    >
                      {deleteCardsIconSVG}
                    </Avatar>
                  </Pressable>
                </HStack>
              </HStack>

              <VStack space={3}>
                <HStack justifyContent="space-between">
                  <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                    SPOC:
                  </Text>
                  <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                    {hotel.hotel_spoc || '-'}
                  </Text>
                </HStack>

                <VStack space={1}>
                  <HStack justifyContent="space-between">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      Venue:
                    </Text>
                    <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                      {hotel.banquet_venue || '-'}
                    </Text>
                  </HStack>
                  {hotel?.location && hotel?.location !== '-' && (
                    <HStack justifyContent="space-between" alignItems="center">
                      <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                        Location:
                      </Text>
                      {hotel?.location !== "-" &&
                        (/^https?:\/\/(www\.)?google\.(com|co\.in)\/maps\/place\//.test(hotel?.location) ||
                          /^https?:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9]+$/.test(hotel?.location) ||
                          /^https?:\/\/(www\.)?google\.(com|co\.in)\/maps\/(place|search)\/[^\/]+\/@[\d\.,]+,([\d\.]+|-[\d\.]+),[\d\.]+z\/data=.*$|^https?:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9]+$/.test(
                            hotel?.location
                          ) ||
                          /^https:\/\//.test(hotel?.location)) ? (
                        <Pressable>
                          <a href={hotel?.location} target="blank" style={{ textDecoration: "none", whiteSpace: "nowrap" }}>
                            <HStack alignItems="center" space={1}>
                              <Svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                                <path
                                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                                  fill="#F37032"
                                />
                              </Svg>
                              <Text
                                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                                color={COLORS.THEME_COLOR}
                                whiteSpace="nowrap"
                                flexShrink={0}
                              >
                                View Location
                              </Text>
                            </HStack>
                          </a>
                        </Pressable>
                      ) : (
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color={COLORS.GRAY_COLOR} whiteSpace="nowrap">
                          {hotel?.location}
                        </Text>
                      )}
                    </HStack>
                  )}
                  <HStack justifyContent="space-between">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      City:
                    </Text>
                    <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      {hotel.city || '-'}
                    </Text>
                  </HStack>
                </VStack>

                <HStack justifyContent="space-between">
                  <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                    Capacity:
                  </Text>
                  <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                    Cluster: {hotel.cluster || '-'} • Theatre: {hotel.theatre || '-'}
                  </Text>
                </HStack>

                <VStack space={2}>
                  <HStack justifyContent="space-between">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      Head Count & Rate:
                    </Text>
                  </HStack>
                  {headCountInfo.breakdown.length > 0 && (
                    <VStack space={1} ml={4}>
                      {headCountInfo.breakdown.slice(0, 6).map((entry, index) => (
                        <Text key={index} fontSize="xs" color={COLORS.GRAY_COLOR} textAlign="left">
                          • <Text fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>{entry.headCount}</Text> : ₹{entry.rate}
                        </Text>
                      ))}
                      {headCountInfo.breakdown.length > 6 && (
                        <Text fontSize="xs" color={COLORS.GRAY_COLOR} textAlign="left">
                          +{headCountInfo.breakdown.length - 6} more
                        </Text>
                      )}
                    </VStack>
                  )}
                </VStack>

                <VStack space={1}>
                  <HStack justifyContent="space-between">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      SPOC Email:
                    </Text>
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      {hotel.spoc_email || '-'}
                    </Text>
                  </HStack>
                  <HStack justifyContent="space-between">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      SPOC Phone:
                    </Text>
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      {hotel.spoc_number || '-'}
                    </Text>
                  </HStack>
                </VStack>

                <VStack space={2}>
                  <HStack justifyContent="space-between">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                      Dishes:
                    </Text>
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                      {dishesInfo.total} items
                    </Text>
                  </HStack>
                  {dishesInfo.breakdown.length > 0 && (
                    <VStack space={0.5} ml={4}>
                      {Array.from({ length: Math.ceil(dishesInfo.breakdown.length / 2) }, (_, rowIndex) => {
                        const startIndex = rowIndex * 2;
                        const rowItems = dishesInfo.breakdown.slice(startIndex, startIndex + 2);
                        return (
                          <Text key={rowIndex} fontSize="xs" color={COLORS.GRAY_COLOR} textAlign="left">
                            {rowItems.map((item, index) =>
                              `${item.category}: ${item.count}`
                            ).join(', ')}
                          </Text>
                        );
                      })}
                    </VStack>
                  )}
                </VStack>

                <HStack justifyContent="space-between">
                  <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.GRAY_COLOR}>
                    Remarks:
                  </Text>
                  <Tooltip label={hotel.remarks || '-'} placement="top">
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} flex={1} textAlign="right" noOfLines={2}>
                      {hotel.remarks || '-'}
                    </Text>
                  </Tooltip>
                </HStack>
              </VStack>
            </Box>
          );
        })}

        {sortedHotels.length === 0 && (
          <Box p={8} textAlign="center">
            <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={COLORS.GRAY_COLOR}>
              No hotels found
            </Text>
          </Box>
        )}

        {/* Pagination Info */}
        {pagination && sortedHotels?.length > 0 && (
          <Box mt={4} textAlign="center">
            <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR}>
              Page {pagination.page} of {pagination.pages} • Total {pagination.total} hotels
            </Text>
          </Box>
        )}

        {/* Load More Button */}
        {hasMore && (
          <Box p={4} display='flex' alignSelf="center" justifyContent='center'>
            <CustomButton
              text={isLoadingMore ? 'Loading...' : 'Load More'}
              onPress={onLoadMore}
              bgColor={COLORS.THEME_COLOR}
              textColor={COLORS.WHITE_COLOR}
              isLoading={isLoadingMore}
              disabled={isLoadingMore}
            />
          </Box>
        )}
      </VStack>
    );
  }

  // Desktop view - table layout
  return (
    <VStack space={4} w="100%">
      {/* Search and Actions */}
      <HStack justifyContent="end" alignItems="center">
        <HStack space={3} alignItems="center">
          {selectedHotels.length > 0 && (
            <CustomButton
              text={`Send Email Enquiry (${selectedHotels.length})`}
              onPress={handleEmailEnquiry}
              bgColor={COLORS.THEME_COLOR}
              textColor={COLORS.WHITE_COLOR}
            />
          )}
        </HStack>
      </HStack>

      {/* Table */}
      <Box bg={COLORS.WHITE_COLOR} borderRadius={8} borderWidth={1} borderColor={COLORS.BORDER_COLOR} overflow="hidden">
        <Box overflowX="auto" style={{
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none'
        }}>
          <table className="hotel-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th className="table-header checkbox-header">
                  <Checkbox
                    isChecked={selectAll}
                    onChange={handleSelectAll}
                    value="selectAll"
                    colorScheme="orange"
                  />
                </th>
                <th
                  className="table-header sortable"
                  onClick={() => handleSort('hotel_name')}
                >
                  <HStack space={2} alignItems="center" justifyContent="space-between">
                    <Text fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>Hotel Name (City)</Text>
                    {getSortIcon('hotel_name')}
                  </HStack>
                </th>
                <th
                  className="table-header sortable"
                  onClick={() => handleSort('banquet_venue')}
                >
                  <HStack space={2} alignItems="center" justifyContent="space-between">
                    <Text fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>Banquet Venue</Text>
                    {getSortIcon('banquet_venue')}
                  </HStack>
                </th>
                <th
                  className="table-header sortable"
                  onClick={() => handleSort('hotel_spoc')}
                >
                  <HStack space={2} alignItems="center" justifyContent="space-between">
                    <Text fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>SPOC Details</Text>
                    {getSortIcon('hotel_spoc')}
                  </HStack>
                </th>
                <th fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT} className="table-header" style={{ textAlign: 'center' }}>Cluster</th>
                <th fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT} className="table-header" style={{ textAlign: 'center' }}>Theatre</th>
                <th fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT} className="table-header" style={{ textAlign: 'left' }}>Head Count & Rate</th>
                <th fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT} className="table-header" style={{ textAlign: 'left' }}>Dishes</th>
                <th fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT} className="table-header">Remarks</th>
                <th fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT} className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedHotels.map((hotel) => {
                const dishesInfo = getDishesBreakdown(hotel.dishes);
                const dishesTooltipText = getDishesTooltipText(hotel.dishes);
                const headCountInfo = getHeadCountBreakdown(hotel.head_count);
                return (
                  <tr key={hotel._id} style={{ borderBottom: `1px solid ${COLORS.BORDER_COLOR}` }}>
                    <td style={{ padding: '20px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <Checkbox
                        isChecked={selectedHotels.includes(hotel._id)}
                        onChange={() => handleHotelSelection(hotel._id)}
                        value={hotel._id}
                        colorScheme="orange"
                      />
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                      <VStack space={1} alignItems="flex-start">
                        <Text
                          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                          color={COLORS.BLACK_COLOR}
                        >
                          {hotel.hotel_name || '-'}
                        </Text>
                        <Text
                          fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                          color={COLORS.GRAY_COLOR}
                        >
                          {'(' + hotel.city + ')' || '-'}
                        </Text>
                      </VStack>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                      <VStack space={1} alignItems="flex-start">
                        <Text
                          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                          color={COLORS.BLACK_COLOR}
                        >
                          {hotel.banquet_venue || '-'}
                        </Text>
                        {hotel?.location && hotel?.location !== '-' && (
                          <HStack alignItems="center" flexWrap="nowrap" minW="fit-content">
                            {hotel?.location !== "-" &&
                              (/^https?:\/\/(www\.)?google\.(com|co\.in)\/maps\/place\//.test(hotel?.location) ||
                                /^https?:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9]+$/.test(hotel?.location) ||
                                /^https?:\/\/(www\.)?google\.(com|co\.in)\/maps\/(place|search)\/[^\/]+\/@[\d\.,]+,([\d\.]+|-[\d\.]+),[\d\.]+z\/data=.*$|^https?:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9]+$/.test(
                                  hotel?.location
                                ) ||
                                /^https:\/\//.test(hotel?.location)) ? (
                              <Pressable>
                                <a href={hotel?.location} target="blank" style={{ textDecoration: "none", whiteSpace: "nowrap" }}>
                                  <HStack alignItems="center" space={1}>
                                    <Svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                                      <path
                                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                                        fill="#F37032"
                                      />
                                    </Svg>
                                    <Text
                                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                      fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                                      color={COLORS.THEME_COLOR}
                                      whiteSpace="nowrap"
                                      flexShrink={0}
                                    >
                                      View Location
                                    </Text>
                                  </HStack>
                                </a>
                              </Pressable>
                            ) : (
                              <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color={COLORS.GRAY_COLOR} whiteSpace="nowrap">
                                {hotel?.location}
                              </Text>
                            )}
                          </HStack>
                        )}
                      </VStack>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                      <VStack space={1} alignItems="flex-start">
                        <Text
                          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                          color={COLORS.BLACK_COLOR}
                        >
                          {hotel.hotel_spoc || '-'}
                        </Text>
                        <Text
                          fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                          color={COLORS.GRAY_COLOR}
                        >
                          {hotel.spoc_email || '-'}
                        </Text>
                        <Text
                          fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                          color={COLORS.GRAY_COLOR}
                        >
                          {hotel.spoc_number || '-'}
                        </Text>
                      </VStack>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <Text textAlign="center">{hotel.cluster || '-'}</Text>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <Text textAlign="center">{hotel.theatre || '-'}</Text>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                      <VStack space={1} alignItems="flex-start">
                        {headCountInfo.breakdown.length > 0 && (
                          <VStack space={0.5} alignItems="flex-start">
                            {headCountInfo.breakdown.slice(0, 5).map((entry, index) => (
                              <Text key={index} className="secondary-text" textAlign="left">
                                <Text fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>{entry.headCount}</Text> : ₹{entry.rate}
                              </Text>
                            ))}
                            {headCountInfo.breakdown.length > 5 && (
                              <Text className="secondary-text" textAlign="left">
                                +{headCountInfo.breakdown.length - 5} more
                              </Text>
                            )}
                          </VStack>
                        )}
                      </VStack>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <VStack space={1} alignItems="flex-start">
                        <Text className="primary-text" fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                          {dishesInfo.total} items
                        </Text>
                        {dishesInfo.breakdown.length > 0 && (
                          <VStack space={0.5} alignItems="flex-start">
                            {Array.from({ length: Math.ceil(dishesInfo.breakdown.length / 2) }, (_, rowIndex) => {
                              const startIndex = rowIndex * 2;
                              const rowItems = dishesInfo.breakdown.slice(startIndex, startIndex + 2);
                              return (
                                <Text key={rowIndex} className="secondary-text" textAlign="left">
                                  {rowItems.map((item, index) =>
                                    `${item.category}: ${item.count}`
                                  ).join(', ')}
                                </Text>
                              );
                            })}
                          </VStack>
                        )}
                      </VStack>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                      <Tooltip label={hotel.remarks || '-'} placement="top">
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} maxW="200px" noOfLines={2}>
                          {hotel.remarks || '-'}
                        </Text>
                      </Tooltip>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <HStack space={2} justifyContent="center">
                        <Pressable onPress={() => onEditHotel(hotel)}>
                          <Avatar
                            bg={'none'}
                            size={'sm'}
                          >
                            {editIconDetailsPage}
                          </Avatar>
                        </Pressable>
                        <Pressable onPress={() => onDeleteHotel(hotel)}>
                          <Avatar
                            bg={'none'}
                            size={'sm'}
                          >
                            {deleteCardsIconSVG}
                          </Avatar>
                        </Pressable>
                      </HStack>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        {sortedHotels?.length === 0 && (
          <Box p={8} textAlign="center">
            <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={COLORS.GRAY_COLOR}>
              No hotels found
            </Text>
          </Box>
        )}
      </Box>

      {/* Pagination Info */}
      {pagination && sortedHotels?.length > 0 && (
        <Box mt={4} textAlign="center">
          <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR}>
            Page {pagination.page} of {pagination.pages} • Total {pagination.total} hotels
          </Text>
        </Box>
      )}

      {/* Load More Button */}
      {hasMore && (
        <Box p={4} display='flex' alignSelf="center" justifyContent='center'>
          <CustomButton
            text={isLoadingMore ? 'Loading...' : 'Load More'}
            onPress={onLoadMore}
            bgColor={COLORS.THEME_COLOR}
            textColor={COLORS.WHITE_COLOR}
            isLoading={isLoadingMore}
            disabled={isLoadingMore}
          />
        </Box>
      )}
    </VStack>
  );
};

export default HotelTable; 